import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import {AlertTriangle, CheckCircle2, Shield, Clock, Zap, Lock} from 'lucide-react'
import { lumi } from "./lib/lumi"

function FinalDeAno() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" })
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError("")

    try {
      const result = await lumi.functions.invoke('saveLead', {
        method: 'POST',
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          origin: 'finaldeano'
        }
      })

      console.log("Lead saved:", result)
      
      // Redirecionar para página de download com token na URL
      if (result?.downloadToken) {
        navigate(`/download?token=${result.downloadToken}`)
      } else {
        setError('Erro ao gerar token de download')
      }
    } catch (err: any) {
      const errorMessage = err?.data?.error || 'Erro ao cadastrar. Tente novamente.'
      setError(errorMessage)
      console.error("Error saving lead:", err)
    } finally {
      setLoading(false)
    }
  }

  const golpesComuns = [
    "Cair em falsas promoções e lojas temporárias",
    "Perder dinheiro em aluguel ou viagem falsa",
    "Entregar produto após comprovante falso de Pix",
    "Clicar em sorteios e brindes \"de Natal\"",
    "Acreditar em falsas centrais de banco",
  ]

  const conteudoEbook = [
    "Os golpes mais comuns do final de ano",
    "Sinais de alerta que quase ninguém percebe",
    "Regras simples pra não decidir sob pressão",
    "Checklist rápido pra usar antes de qualquer pagamento",
  ]

  const caracteristicasFinalAno = [
    "Dinheiro extra na conta",
    "Menos atenção aos detalhes",
    "Emoção acima da razão",
    "Pressa pra resolver tudo rápido",
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-red-50">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center px-6 py-12">
        <div className="max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-8 items-center"
          >
            {/* Conteúdo à esquerda */}
            <div className="text-center md:text-left">
              {/* Alert Badge */}
              <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <AlertTriangle className="w-4 h-4" />
                <span>ALERTA GRATUITO</span>
              </div>

              {/* Headline */}
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                Golpes de Final de Ano:<br />
                <span className="text-red-600">O que mais faz vítimas quando o dinheiro começa a circular</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed">
                13º salário, promoções, viagens, Pix, pressa…<br />
                É exatamente assim que as pessoas caem — mesmo achando que "nunca cairiam".
              </p>

              {/* Primary CTA */}
              <motion.a
                href="#formulario"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-red-600 hover:bg-red-700 text-white text-lg md:text-xl font-bold px-10 py-4 rounded-full shadow-2xl transition-all duration-300 inline-flex items-center gap-3"
              >
                <span>👉 Baixar o alerta gratuito agora</span>
              </motion.a>
            </div>

            {/* Ebook Mockup à direita */}
            <div className="flex justify-center md:justify-end">
              <img
                src="https://static.lumi.new/09/0984d5e9687b222625b7d40c0e22bf12.png"
                alt="Golpes de Final de Ano - Ebook"
                className="w-72 md:w-96 drop-shadow-2xl"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              "Isso acontece com gente normal"
            </h2>

            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-lg p-8 text-left">
              <p className="text-lg text-gray-800 mb-6">
                <strong>Final de ano junta tudo que golpista espera:</strong>
              </p>

              <div className="space-y-3 mb-6">
                {caracteristicasFinalAno.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-orange-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <p className="text-xl font-bold text-gray-900 border-t border-orange-200 pt-6">
                Golpe não acontece por burrice.<br />
                <span className="text-orange-600">Acontece por estado mental errado no momento errado.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* O que você vai evitar */}
      <section className="py-20 px-6 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            O que este material te ajuda a evitar 👇
          </h2>
          <p className="text-center text-gray-600 mb-12">Sem prometer proteção absoluta. Sem "nunca mais".</p>

          <div className="grid md:grid-cols-2 gap-4">
            {golpesComuns.map((golpe, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-6 shadow-md border-l-4 border-red-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-800">{golpe}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prova de Utilidade */}
      <section className="py-20 px-6 bg-gray-900">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Não é teoria. É padrão de golpe.
          </h2>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20">
            <p className="text-lg text-gray-200 leading-relaxed">
              Este guia não ensina golpes.<br />
              Ele ensina como golpistas <strong className="text-orange-400">pensam</strong> e criam <strong className="text-orange-400">pressa, medo e confiança falsa</strong>.
            </p>
            <p className="text-xl text-white font-semibold mt-6">
              Quando você reconhece o padrão, o golpe perde força — mesmo quando muda de formato.
            </p>
          </div>
        </div>
      </section>

      {/* Conteúdo do Ebook */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            O que você vai receber ao baixar agora:
          </h2>
          <p className="text-center text-gray-600 mb-12">Formato curto, direto e prático. Leitura rápida. Aplicação imediata.</p>

          <div className="grid md:grid-cols-2 gap-6">
            {conteudoEbook.map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-sky-200"
              >
                <div className="flex items-start gap-4">
                  <CheckCircle2 className="w-7 h-7 text-blue-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-800 text-lg font-medium">{item}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulário de Captura */}
      <section id="formulario" className="py-20 px-6 bg-gradient-to-br from-red-600 to-orange-600">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl">
            <div className="text-center mb-8">
              <Lock className="w-16 h-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Receba o alerta gratuito agora
              </h2>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
                <p className="text-red-700">{error}</p>
              </div>
            )}

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                    Nome
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none text-lg"
                    placeholder="Seu nome completo"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none text-lg"
                    placeholder="seu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-4 border-2 border-gray-300 rounded-lg focus:border-red-500 focus:outline-none text-lg"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-xl font-bold px-8 py-5 rounded-full shadow-xl transition-all duration-300 flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <>
                      <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <span>👉 Quero evitar golpes de final de ano</span>
                  )}
                </motion.button>

                <p className="text-center text-sm text-gray-600 mt-4">
                  Sem spam. Conteúdo prático.<br />
                  Você pode sair da lista quando quiser.
                </p>
              </form>
            ) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Obrigado, {formData.name}!
                </h3>
                <p className="text-lg text-gray-700">
                  Enviamos o material para <strong>{formData.email}</strong><br />
                  Confira sua caixa de entrada (e spam também).
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Transição para Produto Pago */}
      <section className="py-20 px-6 bg-sky-50">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-lg border-2 border-sky-200">
            <p className="text-lg text-gray-700 mb-6">
              Este material cobre os <strong>golpes mais comuns do final de ano</strong>.
            </p>

            <p className="text-xl text-gray-900 font-semibold mb-6">
              No ebook completo <span className="text-sky-600">Anti-Golpe Digital</span>, você aprende a:
            </p>

            <div className="space-y-4">
              {[
                "Identificar golpes em qualquer época do ano",
                "Reconhecer padrões psicológicos usados por golpistas",
                "Se proteger mesmo quando o golpe muda de cara",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Zap className="w-6 h-6 text-sky-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-800">{item}</span>
                </div>
              ))}
            </div>

            <a
              href="/"
              className="mt-8 inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold px-8 py-4 rounded-full transition-all duration-300"
            >
              Conhecer o guia completo →
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 px-6 text-center text-sm">
        <p className="mb-2">© {new Date().getFullYear()} Guia Anti-Golpe. Todos os direitos reservados.</p>
        <div className="flex justify-center gap-6">
          <a href="#" className="hover:text-white transition-colors">Política de Privacidade</a>
          <a href="#" className="hover:text-white transition-colors">Aviso Legal</a>
        </div>
      </footer>
    </div>
  )
}

export default FinalDeAno